import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Event, NavigationEnd, Router, RouterEvent, RouterModule } from '@angular/router';
import { Store, select } from '@ngrx/store';
import SplitIO from '@splitsoftware/splitio-browserjs/types/splitio';
import { Subject, filter, take, takeUntil } from 'rxjs';
import { UserData } from '../../../dtos/response/login-response/user-data';
import { UserPermissions } from '../../../dtos/response/login-response/user-permissions';
import { BaggageDrawerComponent } from '../../../features/baggage/baggage-drawer/baggage-drawer.component';
import { ResourceCenterDrawerComponent } from '../../../features/resource-center/resource-center-drawer/resource-center-drawer.component';
import { MessageKey } from '../../../models/message/message-key';
import { RichMessage } from '../../../models/message/rich-message';
import { setShowCheckedBagsDrawer } from '../../../services/baggage-service/state/baggage-service.actions';
import { getAllBagsAndAncillaryFees } from '../../../services/fee-service/state/fee.actions';
import { KontentCMSServiceStore } from '../../../services/kontent-cms/kontent-cms-service.store';
import { logout } from '../../../services/login-service/state/login-service.actions';
import { getUser } from '../../../services/login-service/state/login-service.selector';
import { LoginServiceState } from '../../../services/login-service/state/login-service.state';
import {
  addGlobalMessage,
  removeGlobalMessage,
  removeGlobalMessages,
  resetJetStreamShortMessages,
} from '../../../services/message-service/state/message.actions';
import { MessageSelectors } from '../../../services/message-service/state/message.selectors';
import { ReservationServiceSelectors } from '../../../services/reservation-service/state/reservation-service.selectors';
import { getTravelAdvisories } from '../../../services/travel-advisories-service/state/travel-advisories-service.actions';
import { getActiveTravelAdvisoriesCount } from '../../../services/travel-advisories-service/state/travel-advisories-service.selectors';
import { RootState } from '../../../state/state';
import { DrawerService } from '../../mat-drawer/drawer.service';
import { NavBarComponent } from '../../ux/components/nav-bar/nav-bar.component';
import { CommonModule } from '@angular/common';
import { MessageBarsModule } from '../../message-bars/message-bars.module';
import { UXModule } from '../../ux/ux.module';
import { RecentConfirmationCodesComponent } from '../recent-confirmation-codes/recent-confirmation-codes.component';
import { GlobalSearchBarComponent } from '../global-search-bar/global-search-bar.component';
import { SpinnerComponent } from '../spinner/spinner.component';

@Component({
  selector: 'app-common-header-layout',
  templateUrl: './common-header-layout.component.html',
  styleUrls: ['./common-header-layout.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    UXModule,
    MessageBarsModule,
    SpinnerComponent,
    NavBarComponent,
    RecentConfirmationCodesComponent,
    GlobalSearchBarComponent,
  ],
})
export class CommonHeaderLayoutComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly loginServiceStore = inject<Store<LoginServiceState>>(Store);
  private readonly store = inject<Store<RootState>>(Store);

  agentName: string;
  user: UserData;
  selectedValue: string;
  routeUrl: string;
  menuId: string;
  attributes: SplitIO.Attributes;
  hasItinerary: boolean;
  isGroupReservation: boolean;
  passengerCount: number;
  messages: RichMessage[] = [];

  onDestroy$ = new Subject<void>();

  menuItems = [
    { id: 'dashboard', label: 'Dashboard', link: '/home/dashboard', icon: 'dashboard-stroke', iconCategory: 'interface' },
    { id: 'reservation-search', label: 'Reservation search', link: '/search', icon: 'reservation-stroke', iconCategory: 'interface' },
    {
      id: 'book-flights',
      label: 'Book flights',
      link: '/book-flights/flight-search/one-way',
      icon: 'plane-up-stroke',
      iconCategory: 'terminal',
      isBeta: true,
    },
    {
      id: 'guest-profile-search',
      label: 'Guest profile search',
      link: '/mileage-plan-profile-search',
      icon: 'guest-stroke',
      iconCategory: 'interface',
    },
    { id: 'ticket-search', label: 'Ticket search', link: '/coupon-record-search', icon: 'ticket-stroke', iconCategory: 'interface' },
  ];

  kontentCmsServiceStore = inject(KontentCMSServiceStore);
  drawerService = inject(DrawerService);

  ngOnInit(): void {
    this.store.dispatch(removeGlobalMessages());
    this.store.pipe(select(ReservationServiceSelectors.getHasItinerary)).subscribe((hasItinerary) => {
      this.hasItinerary = hasItinerary;
    });
    this.store.pipe(select(ReservationServiceSelectors.getIsGroupReservation)).subscribe((isGroupReservation) => {
      this.isGroupReservation = isGroupReservation;
    });
    this.store.pipe(select(ReservationServiceSelectors.getPassengersCount)).subscribe((passengerCount) => {
      this.passengerCount = passengerCount;
    });
    this.store.dispatch(getTravelAdvisories());
    this.store.pipe(select(getActiveTravelAdvisoriesCount), takeUntil(this.onDestroy$)).subscribe((advisoryCount) => {
      if (advisoryCount > 0) {
        if (advisoryCount === 1) {
          this.store.dispatch(addGlobalMessage(MessageKey.ONE_TRAVEL_ADVISORY));
        } else {
          this.store.dispatch(addGlobalMessage(MessageKey.MULTIPLE_TRAVEL_ADVISORY, [advisoryCount]));
        }
      }
    });

    this.store.pipe(select(MessageSelectors.getGlobalMessagesSortedByPriority), takeUntil(this.onDestroy$)).subscribe((messages) => {
      this.messages = messages;
    });

    // remove any inline styles from body applied by alaska-navigation element on initial load
    document.querySelector('body')?.removeAttribute('style');

    // update the menu indicator when deep linking to reservation summary page
    this.updateActiveMenuIndicatior();

    this.loginServiceStore.pipe(select(getUser), take(1)).subscribe((user: UserData) => {
      this.agentName = `${user.firstName} ${user.lastName}`;
      this.user = user;
      // Check if the agent has limited permissions
      // This can happen if the agent's Sabre profile is setup incorrectly
      if (user.permissions?.length === 0 || !user.sabreAAA) {
        this.store.dispatch(addGlobalMessage(MessageKey.LIMITED_PERMISSIONS, [user.username]));
      }
      // Check if the agent is missing overbook permissions (all agent should have this permission)
      // Overbook EPR = OBCINH
      // Only check if the agent has permissions
      if (user.permissions?.length > 0 && !user.permissions?.includes(UserPermissions.OBCINH)) {
        this.store.dispatch(addGlobalMessage(MessageKey.MISSING_OBCINH_PERMISSION, [user.username]));
      }
    });

    this.router.events
      .pipe(
        filter((e: Event | RouterEvent): e is RouterEvent => e instanceof NavigationEnd),
        takeUntil(this.onDestroy$)
      )
      .subscribe((e: RouterEvent) => {
        if (e.url.startsWith('/reservation')) {
          this.updateActiveMenuIndicatior();
        }
      });
  }

  ngOnDestroy(): void {
    this.store.dispatch(removeGlobalMessages());
    this.onDestroy$.next();
  }

  openDrawerHelper({ tagName, id }: any) {
    const drawerElement = document.querySelector('#' + id);
    if (drawerElement) {
      drawerElement.removeAttribute('selected');
    }
    if (tagName === '#drawerBaggage') {
      // Open the baggage drawer
      this.drawerService.open({
        component: BaggageDrawerComponent,
        width: this.drawerService.defaultWidth,
        isCustomHeader: false,
        title: BaggageDrawerComponent.baggageDrawerTitle,
      });
      this.store.dispatch(resetJetStreamShortMessages());
      if (this.hasItinerary && !this.isGroupReservation && this.passengerCount < 9) {
        this.store.dispatch(getAllBagsAndAncillaryFees());
      }
      return;
    }

    if (tagName === '#resourceCenter') {
      // Open the resource center drawer
      this.drawerService.open({
        component: ResourceCenterDrawerComponent,
        width: this.drawerService.defaultWidth,
        isCustomHeader: false,
        title: 'Resource center',
      });
      return;
    }
  }

  messageClosed(message: RichMessage) {
    this.store.dispatch(removeGlobalMessage(message));
  }

  logout() {
    this.loginServiceStore.dispatch(logout());
  }

  userProfileClick(id) {
    document.querySelector('#' + id)?.removeAttribute('selected');
  }

  updateActiveMenuIndicatior() {
    this.route.firstChild?.data.pipe(take(1)).subscribe((val) => {
      if (val && Object.keys(val).length > 0) {
        this.updateActiveMenuIndicatorHelper(val.menuId);
      } else {
        // this following code snippet is to extract route data from nested child lazy modules
        this.updateActiveMenuIndicatorHelper(this.traverseChildRouteTree(this.route.firstChild));
      }
    });
  }

  updateActiveMenuIndicatorHelper(menuId: string) {
    if (menuId) {
      this.menuId = menuId;
      const menuItem = document.querySelector(`#${menuId}`);
      if (menuItem && !menuItem.hasAttribute('selected')) {
        menuItem.setAttribute('selected', 'true');
      }
    }
  }

  // extract activated route data from child modules
  traverseChildRouteTree(route: ActivatedRoute | null) {
    if (!route || !route.routeConfig) {
      return;
    }

    if (route.routeConfig.data && Object.keys(route.routeConfig.data).length > 0) {
      return route.routeConfig.data?.menuId;
    }

    return this.traverseChildRouteTree(route.firstChild);
  }

  drawerClosed() {
    this.store.dispatch(setShowCheckedBagsDrawer(false));
  }
}
